// frontend/src/pages/ItemSetup.jsx

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useTabs } from '../tabs/useTabs';
import useKeyboardSave from '../hooks/useKeyboardSave';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useEditableGridState } from '../hooks/useEditableGrid';
import { withSorReadOnly } from '../components/grid/sorColumns';
import { referencePickerColDef } from '../utils/referencePickerColDef';
import { mirrorItemFieldEdit } from '../utils/itemFieldMirror';
import { formatItemLabel, formatLocationLabel } from '../utils/itemLabel';
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import PageBanner from "../components/common/PageBanner";
import { ICON, Plus, X } from '../icons';
import ItemSuppliersDialog from '../components/supplier/ItemSuppliersDialog';
import Tile from '../components/layout/Tile';
import { useAuth, useCategoryEditing } from '../contexts/AuthContext';
import { ItemFilterBar, useItemFilter } from '../features/item-filter';
import { useTabPersistedState } from '../tabs/useTabPersistedState';
import { useTabParams } from '../tabs/useTabParams';
import { decodeRouteSegment } from '../tabs/routeSegment';
import { useSsrmGrid, buildExportQuery } from '../hooks/useSsrmGrid';
import { useStoreData } from '../contexts/StoreDataContext';
import { sessionApi } from '../services/SessionApiService';
import { trackEvent } from '../hooks/useTracking';
import { useAgGridTheme } from '../utils/agGridTheme';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useFormatters } from '../hooks/useFormatters';
import { useUsers, useSuppliers } from '../hooks/useReferenceData';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import GridExportButton from '../components/common/GridExportButton';
import BulkImportDialog from '../components/common/BulkImportDialog';
import useBulkImport from '../hooks/useBulkImport';
import { DATA } from '../queries/dataChanged';
import ImportButton from '../components/common/ImportButton';
import ProductCategoryPicker from '../components/common/ProductCategoryPicker';
import { categoryPickerColDef } from '../utils/categoryPickerColDef';
import { writesCategory } from '../utils/categoryEditing';
import { flattenItemLocationRow } from './itemSetupRow';
import { PO_STALE_FIELDS, defaultLinkBatch, isItemScoped, saveTargetOf } from './itemSetupFields';
import { parseServiceLevelCell } from '../utils/planningCellParsers';
import { safetyStockColDef } from '../utils/safetyStockColDef';
import { minimumStockColDef } from '../utils/minimumStockColDef';
import { readsSafetyStock } from '../utils/inventoryTargetMode';
import { ITEM_PLANNING_OVERRIDE_FIELDS, itemPlanningColumns } from './itemPlanningColumns';
import { planningParentOf, resolveItemEffective } from '../utils/itemPlanningParent';
import { useStorePlanning } from '../hooks/useStorePlanning';
import { useQueryClient } from '@tanstack/react-query';
import CurrencyChangeConfirmModal from '../components/CurrencyChangeConfirmModal';
import { useResolutionGate } from '../hooks/useResolutionGate';
import { useItemBulkUpdate, useCreateItem, useCreateItemLocation } from '../hooks/useItemMutations';
import { useItemLocationBulkUpdate } from '../hooks/useInventoryMutations';
import { useSupplierCatalogItemBulkUpdate } from '../hooks/useSupplierCatalogItemMutations';
import { translateRequestError } from '../queries/api';
import { ApiError, apiErrorFrom } from '../utils/parseApiError';
import { failedRowsMessage } from '../utils/failedRows';
import PromotionPriceTooltip from '../components/grid/PromotionPriceTooltip';
import { useIsShopify, productColumn } from '../components/grid/productIdentity';
import { useProductGrouping } from '../components/grid/productGrouping';
import { isProductGroupRow } from '../components/grid/productFields';
import { wrapColumnsForSummaryRows } from '../utils/pinnedTotals';
import '../styles/pages.css';
import { lengthLimits } from '../constants/orgFieldLimits';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// Must match backend BulkOperationMixin.MAX_BULK_ITEMS (shared by every
// store-scoped bulk_update endpoint, incl. ItemViewSet / ItemLocationViewSet).
const BULK_LIMIT = 200;
// Mirrors api.models.Item ItemCode / shopify_sku / ItemName (see lengthLimits).
const ADD_ITEM_FIELD_MAX = { itemCode: 50, sku: 255, itemName: 1029 };

// Which rows carry the Default supplier control. A grouped Shopify view puts
// product rows (`child_count`) above the variants; a supplier belongs to the
// variant, so a group row gets no button — and must not get the cell styling
// that advertises one. One predicate so the renderer and the class rule
// cannot drift apart.
const opensSuppliersDialog = (row) => !isProductGroupRow(row);

function ItemSetup() {
  const { t } = useTranslation();
  const { openTab, setOwnTitle } = useTabs();
  // Drill-through (pages/itemSetupLinks.js): /item-setup/item/:itemCode from Data
  // Health. A route SEGMENT, not a query param — tabs are keyed by pathname, so
  // a `?item_code=` opened through openTab would match no route at all.
  const { itemCode: itemParam } = useTabParams();
  const { selectedStore } = useAuth();
  const isShopify = useIsShopify();
  // What a category write means on this store — which field carries it, and
  // whether the merchant may mint a value (core.category_editing). The two
  // category columns below ask it about THEMSELVES rather than re-reading the
  // store's mode.
  //
  // `null` until a store is selected (auth bootstrap; App.jsx's guards let a
  // page render before that), and `writesCategory` answers false throughout —
  // so `item_type` stays the plain Shopify field it is on every store (that
  // write is legal everywhere) and the category column stays read-only rather
  // than offering a picker whose write half the stores refuse.
  const categoryEditing = useCategoryEditing();
  // Pending edits live in changedCellsRef as the source of truth.
  // changedCells mirrors the ref into React state so re-renders pick up new
  // entries (Map identity changes; ref mutation alone wouldn't re-render).
  // Each blue cell gets its own entry keyed by `cell_<ItemLocationID>_<field>`
  // so the "Save (N)" counter matches what the merchant sees on screen.
  // Entries carry `scope` ('item' | 'il'); save logic buckets back to one
  // request per item or item-location, so item-level fields (cost, sell price,
  // ItemName, ...) still produce a single PATCH even when the item appears on
  // multiple location rows — the backend's unique(ItemID, ValidFrom, tenant)
  // on ItemCostHistory still sees one insert.
  // Bulk-save dirty tracking via the unified primitive hook. Save flow
  // stays custom because ItemSetup chains two endpoints with a confirm
  // interrupt (the PO-conflict 409 on items) — see handleSaveChanges below.
  const editing = useEditableGridState({ getRowId: (row) => row.item_location_id });
  const {
    gridContext, recordCellChange, getChangedCellsRef,
    clearAll: clearAllEdits, clearCellsByPredicate,
  } = editing;

  const queryClient = useQueryClient();
  const itemBulkMutation = useItemBulkUpdate();
  const ilBulkMutation = useItemLocationBulkUpdate();
  const linkBulkMutation = useSupplierCatalogItemBulkUpdate();
  // Keep `saving` as a local flag because the chained-save flow spans
  // both mutations + a confirm() interstitial; isPending of either alone
  // wouldn't capture the whole interactive session.
  const [saving, setSaving] = useState(false);
  // Cost-of-record confirmation (assigning an item's first supplier, or
  // electing another default, moves its cost — Model B). The page hosts the
  // modal; the trigger and the retry live in the Suppliers dialog, which
  // receives this gate as a prop. Nothing the grid itself saves can raise it.
  const currencyGate = useResolutionGate();
  const { confirm, dialogProps } = useConfirmDialog();
  const { data: users, error: usersError } = useUsers();
  const { data: suppliers, error: suppliersError } = useSuppliers();
  // The store's planning defaults — what a row with no default supplier
  // inherits. Those rows' inventory-target cells stay read-only until this
  // is known (itemPlanningColumns.js); a failed read is said out loud below.
  const { planning: storePlanning, isError: storePlanningError } = useStorePlanning();
  // Surface a single banner if a reference dataset failed to load.
  // The hooks each track their own error; we merge for display only.
  const refDataError = (usersError || suppliersError)
    ? t('common.refDataFailed')
    : storePlanningError
      ? t('itemSetup.messages.storePlanningFailed')
      : null;
  const [, setTotalCount] = useState(0);
  const columnTypes = useColumnTypes();
  const defaultHidden = useMemo(() => ['weight', 'length', 'width', 'height'], []);
  const { formatNumber, parseNumber } = useFormatters();
  const pageSize = 100;
  // The add dialog's location picker; the filter bar reads its own.
  const { locations, error: locationsError } = useStoreData();

  // Filters — per-tab via useTabPersistedState so two ItemSetup tabs
  // can hold independent location/group/search/group-mode (survives F5).
  const [selectedLocation, setSelectedLocation] = useTabPersistedState('selectedLocation', "all");
  const [selectedProductCategory, setSelectedProductCategory] = useTabPersistedState('selectedProductCategory', null);
  // Arrow indirection: guardUnsavedChanges is declared below (it needs the
  // editable-grid state) — the hook only invokes it, never during render.
  const itemFilter = useItemFilter({
    seed: decodeRouteSegment(itemParam),
    beforeChange: () => guardUnsavedChanges(),
  });
  // A drill-through tab names the filter it opened on ("Items — APP1KG"), so
  // two Items tabs are tellable apart in the strip. Keyed on the FILTER, not
  // the route: once the merchant clears or changes it, a title still naming
  // the opening value would be a lie.
  const drillThroughFilter = itemFilter.displayText;
  const isDrillThrough = !!itemParam;
  useEffect(() => {
    if (!isDrillThrough) return;
    setOwnTitle(drillThroughFilter
      ? t('itemSetup.filteredTabTitle', { filter: drillThroughFilter })
      : t('nav.items'));
  }, [isDrillThrough, drillThroughFilter, setOwnTitle, t]);
  // Arrow indirection on the guard, as for the filter above.
  const grouping = useProductGrouping({
    gridId: 'item_setup',
    preferenceKey: 'item_setup_grouped',
    defaultHidden,
    leafIdFields: ['item_location_id'],
    beforeToggle: () => guardUnsavedChanges(),
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridTheme = useAgGridTheme();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // The item whose Suppliers dialog is open — `{itemId, label, itemCode}`
  // (components/supplier/ItemSuppliersDialog.jsx), null when closed. Opened
  // through a ref so the column defs (memoised on reference data) need not
  // rebuild for a handler that reads the grid's dirty state.
  const [suppliersDialogItem, setSuppliersDialogItem] = useState(null);
  const openSuppliersDialogRef = useRef(null);
  const [addForm, setAddForm] = useState({ itemCode: '', sku: '', itemName: '', itemType: '', brand: '', barcode: '', productCategory: null, user: null, supplier: null, sellPrice: '', costPrice: '', npiFlag: true, active: true, location: null });
  const createItemMutation = useCreateItem();
  const createItemLocationMutation = useCreateItemLocation();
  const addLoading = createItemMutation.isPending || createItemLocationMutation.isPending;

  const gridApiRef = useRef(null);

  const exportProps = {
    endpoint: '/api/items/export/',
    filename: 'items',
    getParams: useCallback((scope) => (scope === 'all' ? '' : buildExportQuery({
      ...itemFilter.params,
      product_category_id: selectedProductCategory?.id || null,
    })), [selectedProductCategory, itemFilter.params]),
  };

  // Import — update-only Manexys-owned fields (New item / Product category /
  // Brand) on the shared import rail; channel-owned columns are ignored with
  // a notice, and category edits push to Shopify per row like grid edits.
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const importHook = useBulkImport({
    modelName: 'items',
    endpoint: '/api/items/import/',
    changes: [DATA.items],
  });

  const createColumnDefs = useCallback(() => {
    const currencySuffix = selectedStore?.currency ? ` (${selectedStore.currency})` : '';
    // A store may have no currency, but weight_unit / dimension_unit always
    // have a value (core.models.Store defaults, authentication/store_payload.py),
    // so only a missing store drops their suffix.
    const weightSuffix = selectedStore?.id ? ` (${selectedStore.weight_unit})` : '';
    const dimensionSuffix = selectedStore?.id ? ` (${selectedStore.dimension_unit})` : '';
    // NOTE: number formatting for price columns comes from decimalColumn
    // (useColumnTypes). No local override needed — kept below columns as
    // type: 'decimalColumn' which already applies formatNumber + parseNumber.

    // Rows whose effective inventory target mode leaves the safety stock and
    // the service level that sizes it inert. The mirror of the muting
    // itemPlanningColumns applies to the days-of-supply target under Safety
    // stock — a screen that dimmed only one direction would present two live
    // knobs that drive nothing.
    const safetyStockNotRead = (row) => (
      !!row && !isProductGroupRow(row) && !readsSafetyStock(row.effective_inventory_target_mode)
    );

    // A hover that names a Shopify setting is true only where the store's
    // channel is Shopify; an ERP store sees the bare header.
    const shopifyHover = (key) => (isShopify ? t(key) : undefined);
    // The three stock flags are Shopify's on a Shopify store and Manexys's
    // own on an ERP store, so each channel reads its own sentence.
    const channelHover = (shopifyKey, erpKey) => t(isShopify ? shopifyKey : erpKey);
    const columns = [
      // --- Item Identifiers ---
      // Shopify grouped view: the product tree (productGrouping), then one
      // Product cell per variant row (the variant title; the product row
      // carries the picture) and the editable SKU column beside it, as in the
      // flat view below.
      ...groupedColumns([
        variantProductColumn({ skuField: null }),
        {
          field: "shopify_sku",
          editable: true,
          headerName: t("itemSetup.columns.sku"),
          type: 'optionalTextColumn',
        },
      ]),
      // Shopify flat view: a merchant-facing Product cell (image + name)
      // replaces the internal item_code and name columns. The SKU stays a
      // SEPARATE column here (not folded into the cell) because on this
      // master-data screen shopify_sku is editable and syncs back to Shopify.
      ...(isShopify && !isGrouped ? [
        // ItemSetup is the one grid that shows SKU as a SEPARATE column (below)
        // rather than in the Product cell — its shopify_sku is editable here
        // (syncs to Shopify), so it stays an editable column. Cell = image + name.
        productColumn({ headerName: t('itemSetup.columns.product'), skuField: null }),
        {
          field: "shopify_sku",
          editable: true,
          headerName: t("itemSetup.columns.sku"),
          type: 'optionalTextColumn',
        },
      ] : []),
      // Non-Shopify (ERP / manual) stores: item_code is a real, merchant-known
      // value — keep the explicit identifier columns.
      ...(!isShopify ? [
        {
          field: "item_code",
          headerName: t("itemSetup.columns.itemCode"),
        },
        {
          field: "shopify_sku",
          editable: true,
          headerName: t("itemSetup.columns.sku"),
          type: 'optionalTextColumn',
        },
        {
          field: "item_name",
          editable: !isShopify,
          headerName: t("itemSetup.columns.itemName"),
        },
      ] : []),
      // --- Location / Supplier ---
      {
        field: "location_name",
        headerName: t("inventory.columns.location"),
      },
      // --- Inventory target (item-location overrides over the default
      // supplier's, else the store's, setting — itemPlanningColumns.js) ---
      ...itemPlanningColumns({
        t, formatNumber, parseNumber, planning: storePlanning,
      }),
      {
        // The item's DEFAULT supplier — read-only in the grid. ONE click
        // anywhere in the cell opens the per-item Suppliers dialog, where a
        // supplier is assigned with its terms, made default or removed. Bulk
        // assignment lives on the Supplier catalog ("Assign items") and in the
        // import.
        // Sorts by the default-relation name (the colId the server orders on);
        // the value is that name, so a copy carries text.
        field: 'default_supplier',
        colId: 'default_supplier_name',
        headerName: t("itemSetup.columns.defaultSupplier"),
        headerTooltip: t("common.tooltips.defaultSupplier"),
        editable: false,
        valueGetter: (params) => params.data.default_supplier?.supplier_name ?? '',
        // The cell is a control, and `editable: false` means the automatic box
        // (gridDefaults.js `cellStateClassRules`) cannot know that — the column
        // says so itself. Only on rows that render the button: a grouped
        // product row has no supplier of its own to open.
        cellClassRules: {
          'ag-cell-opens-dialog': (params) => opensSuppliersDialog(params.data),
        },
        cellRenderer: (params) => {
          const row = params.data;
          if (!opensSuppliersDialog(row)) return null;
          const def = row.default_supplier;
          const manage = () => openSuppliersDialogRef.current?.(row);
          if (!def) {
            return (
              <button
                type="button"
                className="cell-dialog-button item-supplier-cell item-supplier-cell--assign"
                onClick={manage}
              >
                <Plus size={ICON.sm} />
                <span className="cell-dialog-button__label">{t('itemSetup.supplierCell.assign')}</span>
              </button>
            );
          }
          // "+N" says how many suppliers the item has beyond its default,
          // which the row otherwise hides. No hover text and no icon: the name
          // IS the control.
          const others = (row.related_supplier_ids || [])
            .filter((id) => id !== def.supplier_id).length;
          return (
            <button type="button" className="cell-dialog-button item-supplier-cell" onClick={manage}>
              <span className="cell-dialog-button__label">{def.supplier_name}</span>
              {others > 0 && <span className="item-supplier-cell__more">+{others}</span>}
            </button>
          );
        },
      },
      {
        field: "supplier_sku",
        headerName: t("itemSetup.columns.supplierSku"),
        headerTooltip: t("itemSetup.tooltips.supplierSku"),
        // The DEFAULT relation's code — each supplier has its own. Saved to
        // that relation, or to every relation of the item when the merchant
        // says so on save (askSupplierScope), so a row with no default
        // supplier — and a grouped product row — has nothing to edit.
        editable: (params) => params.data.default_supplier_link_id != null,
        width: 140,
      },
      {
        // The DEFAULT relation's pack size, under the name the Supplier
        // catalog gives it. Saved like the supplier SKU above.
        field: "order_multiple",
        headerName: t("supplierCatalog.columns.orderMultiple"),
        headerTooltip: t("itemSetup.tooltips.orderMultiple"),
        type: "numberColumn",
        editable: (params) => params.data.default_supplier_link_id != null,
        width: 150,
      },
      // --- Item Classification ---
      {
        field: "item_type",
        headerName: t("itemSetup.columns.itemType"),
        headerTooltip: channelHover('itemSetup.tooltips.itemTypeShopify', 'itemSetup.tooltips.itemType'),
        // Shopify's "Product type". Writes push productUpdate.productType, which
        // is keyed on shopify_product_gid — so an item with no channel product
        // can't be given one (same gate as the category column below). It is
        // PRODUCT-level: editing it changes every variant of that product, and
        // the backend mirrors that onto the sibling rows after the push lands.
        //
        // When the STORE writes its category through this field, this column IS
        // the category control (the category column below is its read-only
        // mirror), so it gets the shared category cell: a typeahead over the
        // types the store already uses, plus a create row. Where the category
        // is written elsewhere, a product type is just a Shopify passthrough
        // field and nothing enumerates the store's types — plain text cell.
        ...(writesCategory(categoryEditing, 'item_type')
          ? categoryPickerColDef({
              capability: categoryEditing,
              field: 'item_type',
              queryClient,
              storeId: selectedStore?.id,
              placeholder: t('productType.searchPlaceholder'),
              createLabel: (name) => t('productType.createOption', { name }),
            })
          : {
              // Trim on entry — a trailing space is a different string to
              // Shopify, and the merchant can't see it.
              valueParser: (params) => String(params.newValue ?? '').trim(),
            }),
        editable: (params) => !isShopify || !!params.data.shopify_product_gid,
        cellRenderer: (params) =>
          !isProductGroupRow(params.data) && isShopify && !params.data.shopify_product_gid
            ? <span style={{ color: 'var(--color-text-muted)' }}>{t('dataHealth.categoryNoChannelProduct', { channel: 'Shopify' })}</span>
            : (params.value || ''),
      },
      {
        ...referencePickerColDef({
          field: 'user',
          colId: 'UserID__email',
          options: users,
          optionId: 'id',
          // Display: full name when present, fall back to email.
          formatLabel: (u) => {
            if (u.first_name || u.last_name) {
              return `${u.first_name || ''} ${u.last_name || ''}`.trim();
            }
            return u.email || '';
          },
          // Searchable by both full name and email so merchants
          // can type either.
          searchableLabels: (u) => [
            (u.first_name || u.last_name)
              ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
              : null,
            u.email,
          ],
          setFkIdField: false,
        }),
        headerName: t("itemSetup.columns.user"),
        headerTooltip: t("itemSetup.tooltips.user"),
        editable: true,
      },
      {
        // Product category. Where the store writes its category HERE, this is
        // the shared category cell — a typeahead over Shopify's standard
        // taxonomy — and writes flow through field_sync.ITEM_SHOPIFY_FIELDS so
        // Shopify stays in sync (productUpdate with
        // deleteConflictingConstrainedMetafields). Where the category is
        // derived from the product type instead, the column is a read-only
        // mirror of that derivation (see `editable` below), so it needs no
        // editor at all — offering one would open a picker whose every write
        // ItemSerializer refuses.
        ...(writesCategory(categoryEditing, 'product_category_id')
          ? categoryPickerColDef({
              capability: categoryEditing,
              field: 'product_category',
              queryClient,
              storeId: selectedStore?.id,
              placeholder: t('itemSetup.columns.productCategory'),
            })
          : { field: 'product_category' }),
        colId: 'product_category__display_label',
        headerName: t('itemSetup.columns.productCategory'),
        headerTooltip: shopifyHover('itemSetup.tooltips.productCategory'),
        // Setting a category on a Shopify store pushes a productUpdate keyed on
        // shopify_product_gid; an item with no channel product (e.g. an ERP item
        // not yet published) can't be pushed and would fail field_sync on save.
        // Gate the picker to display-only for those rows — same pattern as
        // DataHealth's "Set product categories" section. Non-Shopify stores skip
        // the productUpdate gate entirely, so category stays editable there.
        // A product row of the grouped grid carries the product's gid but
        // is no item: nothing to edit. It shows the category its variants
        // share, by label (`_product_fact`, api/filters.py).
        //
        // A store that writes its category through another field never edits
        // this column: the category is derived there, so ItemSerializer rejects
        // a direct write outright (it would be overwritten on the next sync).
        // The field named by the capability is the editable one.
        editable: (params) => !isProductGroupRow(params.data)
          && writesCategory(categoryEditing, 'product_category_id')
          && (!isShopify || !!params.data.shopify_product_gid),
        // Overrides the builder's renderer for the no-channel gate. Reads the
        // picked object off the row directly — the column's valueFormatter
        // emits the id (for the clipboard round-trip), not the label.
        cellRenderer: (params) => {
          if (isProductGroupRow(params.data)) return params.data.category_label;
          // String is "Not in {{channel}}"; this branch is Shopify-only.
          if (isShopify && !params.data.shopify_product_gid) {
            return <span style={{ color: 'var(--color-text-muted)' }}>{t('dataHealth.categoryNoChannelProduct', { channel: 'Shopify' })}</span>;
          }
          return params.data.product_category?.display_label || '—';
        },
      },
      {
        field: "brand",
        headerName: t("itemSetup.columns.brand"),
        headerTooltip: shopifyHover('itemSetup.tooltips.brand'),
        editable: !isShopify,
      },
      // --- Physical attributes ---
      {
        field: "weight",
        headerName: t("itemSetup.columns.weight") + weightSuffix,
        type: 'decimalColumn',
        editable: !isShopify,
      },
      {
        field: "length",
        headerName: t("itemSetup.columns.length") + dimensionSuffix,
        type: 'decimalColumn',
        editable: !isShopify,
      },
      {
        field: "width",
        headerName: t("itemSetup.columns.width") + dimensionSuffix,
        type: 'decimalColumn',
        editable: !isShopify,
      },
      {
        field: "height",
        headerName: t("itemSetup.columns.height") + dimensionSuffix,
        type: 'decimalColumn',
        editable: !isShopify,
      },
      // --- Financials ---
      {
        field: "current_sell_price",
        headerName: t("itemSetup.columns.sellPrice") + currencySuffix,
        headerTooltip: shopifyHover('itemSetup.tooltips.sellPrice'),
        type: 'decimalColumn',
        editable: true,
        cellClassRules: {
          'ag-cell-note-mark': (params) => !!params.data._promotion,
        },
        tooltipComponent: PromotionPriceTooltip,
        tooltipValueGetter: (params) => params.data._promotion ? true : '',
      },
      {
        field: "current_cost_price_supplier_ccy",
        // No currencySuffix here — cost is in each item's operating currency
        // (the default supplier's, else the store's), so it varies per row
        // and a store-ccy suffix would mislead. The adjacent "Cost ccy"
        // column renders the actual per-row currency.
        headerName: t("itemSetup.columns.costPrice"),
        headerTooltip: t("itemSetup.tooltips.costPrice"),
        type: 'decimalColumn',
        editable: true,
      },
      {
        // Per-row cost currency. Read-only — it is the default supplier's
        // currency, else the store's; changing it would mean switching
        // suppliers.
        field: "cost_currency_code",
        headerName: t("itemSetup.columns.costCurrency"),
        editable: false,
        type: 'optionalTextColumn',
      },
      {
        field: "compare_at_price",
        headerName: t("itemSetup.columns.compareAtPrice") + currencySuffix,
        headerTooltip: shopifyHover('itemSetup.tooltips.compareAtPrice'),
        type: 'decimalColumn',
        editable: true,
        cellClassRules: {
          'ag-cell-note-mark': (params) => !!params.data._promotion,
        },
        tooltipComponent: PromotionPriceTooltip,
        tooltipValueGetter: (params) => params.data._promotion ? true : '',
      },
      // --- ItemLocation Planning Parameters ---
      // MOQ/MaxOQ are per-relation (SupplierItem) — edited on the Supplier
      // catalog screen, not here.
      {
        field: "service_level_pct",
        headerName: t("itemSetup.columns.serviceLevel"),
        headerTooltip: t("common.tooltips.serviceLevel"),
        type: 'decimalColumn',
        editable: true,
        // Refuse out of range, round extra precision, keep the stored value on
        // a blank — stated once in planningCellParsers.js (bounds from
        // utils/serviceLevel.js).
        valueParser: (params) => parseServiceLevelCell(params, parseNumber),
        // The service level only reaches a plan through the safety stock it
        // sizes, so a Days of supply row reads neither. Dimmed and explained
        // rather than hidden: the value is still stored, and still what a
        // flip back to Safety stock will use.
        tooltipValueGetter: (params) => (
          safetyStockNotRead(params.data) ? t('common.safetyStockNotRead') : ''
        ),
        cellClassRules: {
          'ag-cell-not-in-use': (params) => safetyStockNotRead(params.data),
        },
      },
      safetyStockColDef({
        t,
        formatNumber,
        parseNumber,
        headerName: t("inventory.columns.safetyStock"),
        notInUse: safetyStockNotRead,
      }),
      minimumStockColDef({ t, parseNumber }),
      {
        field: "purchasable_flag",
        headerName: t("itemSetup.columns.purchasable"),
        headerTooltip: t("itemSetup.tooltips.purchasable"),
        type: 'booleanColumn',
      },
      // --- Classifications (read-only) ---
      {
        field: "item_classification_abc",
        headerName: t("inventory.columns.abc"),
        headerTooltip: t("reports.classification.tooltips.abcClass"),
      },
      {
        field: "item_classification_xyz",
        headerName: t("inventory.columns.xyz"),
        headerTooltip: t("reports.classification.tooltips.xyzClass"),
      },
      // --- Data quality flags ---
      {
        field: "inventory_tracked",
        headerName: t("itemSetup.columns.inventoryTracked"),
        headerTooltip: channelHover('itemSetup.tooltips.inventoryTrackedShopify', 'itemSetup.tooltips.inventoryTracked'),
        type: 'booleanColumn',
      },
      {
        field: "allows_overselling",
        headerName: t("itemSetup.columns.allowsOverselling"),
        headerTooltip: channelHover('itemSetup.tooltips.allowsOversellingShopify', 'itemSetup.tooltips.allowsOverselling'),
        type: 'booleanColumn',
      },
      {
        field: "requires_shipping",
        headerName: t("itemSetup.columns.requiresShipping"),
        headerTooltip: channelHover('itemSetup.tooltips.requiresShippingShopify', 'itemSetup.tooltips.requiresShipping'),
        type: 'booleanColumn',
      },
      // --- Status flags ---
      {
        field: "item_status",
        headerName: t("itemSetup.columns.active"),
        headerTooltip: shopifyHover('itemSetup.tooltips.active'),
        type: 'booleanColumn',
        // A product row whose variants differ carries no status: no checkbox
        // rather than an unticked one.
        valueGetter: (params) => (params.data.item_status == null ? null : params.data.item_status === 1),
        valueSetter: (params) => {
          params.data.item_status = params.newValue ? 1 : 0;
          return true;
        },
      },
      {
        field: "npi_flag",
        headerName: t("itemSetup.columns.npi"),
        headerTooltip: t("common.tooltips.npi"),
        type: 'booleanColumn',
      },
    ];

    // ERP-owned fields are read-only here when the ERP is their SoR (edited in
    // the ERP) — mirrors backend SoRReadOnlyMixin. Each column maps to its `sor`
    // envelope domain; withSorReadOnly resolves per row, so sell price stays
    // editable when the sales channel owns it. Only already-editable columns are
    // affected (the `=== true` guard never makes a read-only column editable).
    // Cost is absent: an ERP-owned cost is edited here too, and the backend
    // writes it back to the ERP.
    const SOR_FIELDS = {
      item_code: 'item_master', item_name: 'item_master', item_type: 'item_master',
      brand: 'item_master', weight: 'item_master', length: 'item_master',
      width: 'item_master', height: 'item_master',
      product_category: 'product_category',
      current_sell_price: 'sell_price', compare_at_price: 'sell_price',
      supplier_sku: 'item_supplier_link', order_multiple: 'item_supplier_link',
    };
    return wrapColumnsForSummaryRows(columns.map((c) => {
      const domain = SOR_FIELDS[c.field];
      return domain ? withSorReadOnly(c, domain, t) : c;
    }));
  }, [users, t, isShopify, categoryEditing, isGrouped, groupedColumns, variantProductColumn, formatNumber, parseNumber, selectedStore?.id, selectedStore?.weight_unit, selectedStore?.dimension_unit, selectedStore?.currency, queryClient, storePlanning]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t).
  const columnDefs = useMemo(() => createColumnDefs(), [createColumnDefs]);


  const ssrmParams = useMemo(() => ({
    ...itemFilter.params,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
    product_category_id: selectedProductCategory?.id || null,
  }), [itemFilter.params, selectedLocation, selectedProductCategory]);

  // Group headers come back as plain objects; leaf rows need flattening +
  // active-promotion enrichment from the side-channel data.active_promotions,
  // which names the promoted items of THIS block and is empty when none is.
  const transformItemRows = useCallback((data, request) => {
    const rowGroupCols = request?.rowGroupCols || [];
    const groupKeys = request?.groupKeys || [];
    const isGroupRequest = rowGroupCols.length > 0 && groupKeys.length === 0;
    if (isGroupRequest) return data.results;
    // Build per-scope views of pending edits.
    //  - itemEdits: any one entry per item+field has the value (sibling cells
    //    are mirrored to the same value). Keyed by _ItemID derived from rowData.
    //  - ilEdits: keyed by ItemLocationID so freshly-loaded rows can re-apply
    //    the merchant's typed value — used after the partial-save recovery
    //    path, which preserves IL entries while the grid refetches.
    // Scope re-derived from isItemScoped rather than a stored metadata field.
    const itemEdits = new Map();
    const ilEdits = new Map();
    for (const info of getChangedCellsRef().values()) {
      if (isItemScoped(info.fieldName)) {
        const itemId = info.rowData._ItemID;
        let m = itemEdits.get(itemId);
        if (!m) { m = []; itemEdits.set(itemId, m); }
        m.push(info);
      } else {
        const ilId = info.rowData.item_location_id;
        let m = ilEdits.get(ilId);
        if (!m) { m = {}; ilEdits.set(ilId, m); }
        m[info.fieldName] = info.newValue;
      }
    }
    const transformed = data.results.map((il) => {
      const row = flattenItemLocationRow(il);
      const itemId = row._ItemID;
      if (itemId && data.active_promotions[itemId]) {
        row._promotion = data.active_promotions[itemId];
      }
      // Re-apply pending edits onto freshly-loaded rows so SSRM blocks loaded
      // after an edit (or after the partial-save recovery refetch) reflect the
      // user's unsaved value, not the server's. Item-level edits also seed a
      // cell entry on the new row so blue highlight + "Save (N)" counter stay
      // consistent with what the merchant sees. recordCellChange handles the
      // setChangedCells mirror — no manual ref-and-state dance.
      if (itemId && row.item_location_id) {
        const edits = itemEdits.get(itemId);
        if (edits) {
          for (const info of edits) {
            row[info.fieldName] = info.newValue;
            recordCellChange({
              row,
              fieldName: info.fieldName,
              oldValue: info.oldValue,
              newValue: info.newValue,
            });
          }
        }
      }
      if (row.item_location_id) {
        const ilFields = ilEdits.get(row.item_location_id);
        if (ilFields) {
          for (const [field, value] of Object.entries(ilFields)) {
            row[field] = value;
          }
        }
      }
      return row;
    });
    return transformed;
  }, [getChangedCellsRef, recordCellChange]);

  const handleSsrmData = useCallback((data) => {
    setTotalCount(data.count || 0);
  }, []);

  const { onGridReady, onGridPreDestroyed, refresh, pagination } = useSsrmGrid({
    endpoint: '/api/item-locations/',
    params: ssrmParams,
    pageSize,
    transformRows: transformItemRows,
    onData: handleSsrmData,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
    gridRef: gridApiRef,
  });

  const guardUnsavedChanges = async (messageKey = 'itemSetup.messages.unsavedChangesFilter') => {
    if (editing.isDirty) {
      const ok = await confirm({
        title: t('common.unsavedChangesTitle'),
        message: t(messageKey),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return false;
      clearAllEdits();
    }
    return true;
  };

  // The Suppliers dialog writes immediately and the grid re-reads its rows
  // after each write, so it opens through the same guard a filter change
  // does — a pending edit on the row would otherwise be re-applied over what
  // the dialog just wrote, or lost. Published through a ref, from an effect
  // (never during render), so the memoised column defs call the latest guard
  // without rebuilding on every render.
  useEffect(() => {
    openSuppliersDialogRef.current = async (row) => {
      const wasDirty = editing.isDirty;
      if (!(await guardUnsavedChanges('itemSetup.messages.unsavedChangesSuppliers'))) return;
      // The guard cleared the edits; the cells still show the typed values
      // until the rows are re-read. A filter change re-reads by itself — this
      // path has to, or a dialog closed without a write leaves stale cells.
      if (wasDirty) refresh({ purge: false });
      setSuppliersDialogItem({
        itemId: row._ItemID,
        label: formatItemLabel(row, isShopify),
        itemCode: row.item_code,
      });
    };
  });

  const trackCellChange = useCallback((event) => {
    const updatedItem = event.data;
    const fieldName = event.colDef.field;
    const nodesToRefresh = [event.node];

    if (isItemScoped(fieldName)) {
      if (!updatedItem._ItemID || !updatedItem.item_location_id) return;
      // Cost / sell price / ItemName / default supplier / ... are item-level.
      // The grid renders one row per ItemLocation, so the same item can appear
      // on N rows; mirror the edit onto every loaded sibling row so the display
      // matches what we save (one write per item) and add a cell entry per
      // visible row so the counter matches the blue cells on screen. The save
      // flow re-derives scope from `isItemScoped(fieldName)`, so no metadata
      // rides on the cell entry. mirrorItemFieldEdit mirrors the row's stored
      // field value (the option OBJECT for picker columns), never
      // `event.newValue` (the scalar id) — see its module docstring.
      const siblingNodeByRow = new Map();
      const siblingRows = [];
      if (event.api) {
        event.api.forEachNode((node) => {
          if (node !== event.node && node.data) {
            siblingRows.push(node.data);
            siblingNodeByRow.set(node.data, node);
          }
        });
      }
      const mirroredRows = mirrorItemFieldEdit({
        editedRow: updatedItem,
        fieldName,
        oldValue: event.oldValue,
        siblingRows,
        record: recordCellChange,
      });
      for (const row of mirroredRows) nodesToRefresh.push(siblingNodeByRow.get(row));
    } else {
      if (!updatedItem.item_location_id) return;
      recordCellChange({
        row: updatedItem,
        fieldName,
        oldValue: event.oldValue,
        newValue: event.newValue,
      });
      // The Days-of-supply cell mutes under an effective Safety-stock mode,
      // so a mode edit re-derives the row's readings in the same render.
      if (ITEM_PLANNING_OVERRIDE_FIELDS.includes(fieldName)) {
        resolveItemEffective(updatedItem, planningParentOf(updatedItem.default_supplier, storePlanning));
      }
    }

    if (event.api) {
      event.api.refreshCells({
        force: true,
        rowNodes: nodesToRefresh,
        columns: ITEM_PLANNING_OVERRIDE_FIELDS.includes(fieldName)
          ? [event.column, ...ITEM_PLANNING_OVERRIDE_FIELDS]
          : [event.column],
      });
    }
  }, [recordCellChange, storePlanning]);

  const onCellValueChanged = useCallback(async (event) => {
    const fieldName = event.colDef.field;

    // Warn when editing price fields on an item with active promotion
    const priceFields = ['current_sell_price', 'compare_at_price', 'current_cost_price_supplier_ccy'];
    if (priceFields.includes(fieldName) && event.data._ItemID) {
      try {
        const res = await sessionApi.get(`/api/items/${event.data._ItemID}/active-promotion/`);
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/items/<id>/active-promotion/');
        const promoData = await res.json();
        if (promoData.has_active_promotion) {
          const ok = await confirm({
            title: t('itemSetup.messages.activePromotionTitle'),
            message: t('itemSetup.messages.activePromotionWarning', {
              name: promoData.promotion_name,
              endDate: promoData.end_date,
            }),
            confirmLabel: t('itemSetup.messages.activePromotionConfirm'),
            variant: 'warning',
          });
          if (!ok) {
            event.data[fieldName] = event.oldValue;
            event.api.refreshCells({ force: true, rowNodes: [event.node], columns: [fieldName] });
            return;
          }
        }
      } catch (err) {
        // The lookup is what decides whether this edit needs the promotion
        // warning. Without an answer — refused or never reached — the edit is
        // not applied: a price changed under a live promotion is overwritten
        // when it ends, and letting the edit through would hide that behind a
        // save that looked ordinary.
        console.error('Active-promotion check failed:', err);
        showToast(translateRequestError(err, t, 'itemSetup.messages.activePromotionCheckFailed'), 'error');
        event.data[fieldName] = event.oldValue;
        event.api.refreshCells({ force: true, rowNodes: [event.node], columns: [fieldName] });
        return;
      }
    }

    // item_status is numeric (0/1) on the model, but the grid column exposes
    // it as a boolean via valueGetter — so event.newValue is a boolean here
    // while event.data.item_status is the post-setter number.
    if (isShopify && fieldName === 'item_status') {
      // Deactivate → archives on Shopify
      if (event.newValue === false) {
        const ok = await confirm({
          title: t('itemSetup.messages.archiveTitle'),
          message: t('itemSetup.messages.archiveConfirm'),
          confirmLabel: t('itemSetup.messages.archiveButton'),
          variant: 'danger',
        });
        if (!ok) {
          event.data.item_status = 1;
          event.api.refreshCells({ force: true, rowNodes: [event.node], columns: [fieldName] });
          return;
        }
      } else if (event.newValue === true) {
        // Reactivate → unarchives on Shopify
        const ok = await confirm({
          title: t('itemSetup.messages.reactivateTitle'),
          message: t('itemSetup.messages.reactivateConfirm'),
          confirmLabel: t('itemSetup.messages.reactivateButton'),
          variant: 'success',
        });
        if (!ok) {
          event.data.item_status = 0;
          event.api.refreshCells({ force: true, rowNodes: [event.node], columns: [fieldName] });
          return;
        }
      }
    }

    trackCellChange(event);
  }, [isShopify, confirm, t, trackCellChange]);

  const finishSaveSuccess = ({ hasPoStaleChanges, rowCount }) => {
    showToast(
      hasPoStaleChanges
        ? t("itemSetup.messages.saveSuccessPoStale", { count: rowCount })
        : t("itemSetup.messages.saveSuccess", { count: rowCount }),
      hasPoStaleChanges ? "info" : "success",
    );
    clearAllEdits();
  };

  // A default-supplier field (code, pack size) edited on an item with more
  // than one supplier: ask whether it goes to the default supplier only or to
  // all of the item's suppliers. Resolves 'default' or 'all'
  // (itemSetupFields.js defaultLinkBatch), or null when the merchant cancels.
  const askSupplierScope = async (linkBuckets) => {
    const rows = [...linkBuckets.values()].map(({ rowData }) => rowData);
    if (!rows.some((row) => row.supplier_link_ids.length > 1)) return 'default';
    const [row] = rows;
    const one = rows.length === 1;
    const supplier = row.default_supplier.supplier_name;
    const answer = await confirm({
      title: t('itemSetup.supplierScope.title'),
      message: one
        ? t('itemSetup.supplierScope.questionOne', { supplier })
        : t('itemSetup.supplierScope.questionMany'),
      choices: [
        {
          value: 'default',
          label: one
            ? t('itemSetup.supplierScope.onlySupplier', { supplier })
            : t('itemSetup.supplierScope.defaultOnly'),
        },
        {
          value: 'all',
          label: one
            ? t('itemSetup.supplierScope.allOfItem')
            : t('itemSetup.supplierScope.allOfItems'),
        },
      ],
      confirmLabel: t('btn.apply'),
      variant: 'info',
    });
    return answer ? answer.value : null;
  };

  // --- Save: routes each edit to its endpoint (itemSetupFields.js): item,
  // default supplier relation, item-location ---
  const handleSaveChanges = async () => {
    setSaving(true);
    // Hoisted out of try{} so the catch block can keep exactly the cells the
    // server did not save: the ids each batch reported as saved, per target.
    // Every bulk endpoint commits per row, so a failed batch can still have
    // saved some of its rows.
    const saved = { item: new Set(), defaultLink: new Set(), itemLocation: new Set() };
    const recordSaved = (target, ids) => {
      for (const id of ids) saved[target].add(String(id));
    };
    const succeededIds = (data) => data.results.filter((row) => row.success).map((row) => row.id);
    const isSaved = (info) => {
      const { target, id } = saveTargetOf(info.fieldName, info.rowData);
      return saved[target].has(String(id));
    };
    // The results of the batch whose rows failed — that batch stops the save,
    // so there is at most one.
    let failedResults = null;
    try {
      let hasPoStaleChanges = false;

      // Bucket pending edits by save target. The unified cell map holds
      // one entry per blue cell (keyed by ItemLocationID), so an item-
      // level edit mirrored across N location rows produces N entries —
      // bucketing by _ItemID collapses them back into one save target,
      // which is what /api/items/bulk-update/ requires (unique(ItemID,
      // ValidFrom, tenant) on ItemCostHistory). All sibling entries hold
      // the same newValue (mirroring), so the last-write-wins on rowData
      // is safe. The target is re-derived by saveTargetOf at iteration
      // time — no metadata stored on the cell.
      // target → (id → { rowData, fields: Set })
      const buckets = { item: new Map(), defaultLink: new Map(), itemLocation: new Map() };
      for (const info of getChangedCellsRef().values()) {
        const { fieldName, rowData } = info;
        const { target, id } = saveTargetOf(fieldName, rowData);
        let entry = buckets[target].get(id);
        if (!entry) {
          entry = { rowData, fields: new Set() };
          buckets[target].set(id, entry);
        }
        entry.fields.add(fieldName);
        entry.rowData = rowData;
      }

      const itemBatch = [];
      for (const [itemId, { rowData, fields }] of buckets.item.entries()) {
        if ([...fields].some(f => PO_STALE_FIELDS.has(f))) hasPoStaleChanges = true;
        const payloadFields = {};
        for (const field of fields) {
          if (field === 'user') {
            // Nested user object on Item: { id, email, ... }. Backend
            // expects the PK as ``user_id`` (write-only PrimaryKeyRelatedField).
            payloadFields.user_id = rowData.user?.id || null;
          } else if (field === 'product_category') {
            // Nested ProductCategory object on Item: { id, name,
            // display_label }. Backend expects ``product_category_id`` PK.
            // Unconditional, and safe to be: this field can only be dirty when
            // the column was editable, which is exactly when the store takes
            // its category write on `product_category_id`. Where it doesn't,
            // the dirty field is `item_type` and the generic branch below
            // sends it.
            payloadFields.product_category_id = rowData.product_category?.id || null;
          } else {
            payloadFields[field] = rowData[field];
          }
        }
        itemBatch.push({ id: itemId, fields: payloadFields });
      }

      const ilBatch = [...buckets.itemLocation.entries()].map(([id, { rowData, fields }]) => {
        if ([...fields].some(f => PO_STALE_FIELDS.has(f))) hasPoStaleChanges = true;
        return { id, fields: Object.fromEntries([...fields].map((f) => [f, rowData[f]])) };
      });

      // Guard: reject if any batch exceeds backend limit — counted in edited
      // records, and checked before the supplier question is asked.
      if ([itemBatch.length, buckets.defaultLink.size, ilBatch.length].some((n) => n > BULK_LIMIT)) {
        showToast(t('itemSetup.messages.bulkLimitExceeded', { limit: BULK_LIMIT }), 'warning');
        return;
      }

      // Asked before anything is written, so Cancel leaves the whole save
      // undone and every cell as typed.
      const linkScope = await askSupplierScope(buckets.defaultLink);
      if (linkScope === null) return;
      const linkBatch = defaultLinkBatch(buckets.defaultLink, linkScope);
      if ([...buckets.defaultLink.values()].some(({ fields }) => [...fields].some((f) => PO_STALE_FIELDS.has(f)))) {
        hasPoStaleChanges = true;
      }

      // One bulk request per target instead of one per row (the default-supplier
      // batch may take several — see below).

      // Item bulk update — one gate: 409 confirmation_required, a pending PO
      // recommendation would be invalidated by the change. Prompt + retry with
      // confirmPoDeletion. The endpoint's other gate (200 requires_confirmation,
      // a default-supplier election moving the item's cost) cannot fire from
      // this grid — the supplier is not edited here; the Suppliers dialog
      // hosts that gate through `currencyGate`.
      if (itemBatch.length > 0) {
        let data;
        try {
          data = await itemBulkMutation.mutateAsync({ items: itemBatch });
        } catch (err) {
          if (err instanceof ApiError && err.status === 409 && err.body?.confirmation_required) {
            const ok = await confirm({
              title: t('itemSetup.messages.poConflictTitle'),
              message: t('itemSetup.messages.poConflictConfirm', { count: err.body.pending_po_count }),
              confirmLabel: t('itemSetup.messages.poConflictButton'),
              variant: 'danger',
            });
            if (!ok) {
              // Re-attach the original 409 ApiError as the cause so the
              // rethrow keeps the diagnostic chain intact.
              throw new Error('cancelled', { cause: err });
            }
            data = await itemBulkMutation.mutateAsync({
              items: itemBatch,
              confirmPoDeletion: true,
            });
          } else {
            throw err;
          }
        }

        if (data?.requires_confirmation) {
          // No field this grid edits can raise the cost gate (see above). A
          // body that does is a contract break — say so, never report a save.
          throw new Error('items/bulk-update answered requires_confirmation for a batch with no supplier election');
        }
        recordSaved('item', succeededIds(data));
        if (data.summary.failed > 0) {
          failedResults = data.results;
          throw new Error(`Failed to save ${data.summary.failed} item(s)`);
        }
        if (data?.has_po_stale) {
          hasPoStaleChanges = true;
        }
      }

      if (linkBatch.items.length > 0) {
        // Applied to all of an item's suppliers, the batch holds one row per
        // supplier and can pass the endpoint's per-request cap. The endpoint
        // commits per row, so chunks write exactly what one request would.
        // Results are recorded even when a later chunk throws: the earlier
        // chunks are saved.
        const results = [];
        try {
          for (let start = 0; start < linkBatch.items.length; start += BULK_LIMIT) {
            const data = await linkBulkMutation.mutateAsync({
              items: linkBatch.items.slice(start, start + BULK_LIMIT),
            });
            results.push(...data.results);
          }
        } finally {
          recordSaved('defaultLink', linkBatch.savedIds(results));
        }
        const failed = results.filter((row) => !row.success).length;
        if (failed > 0) {
          failedResults = results;
          throw new Error(`Failed to save ${failed} default supplier relation(s)`);
        }
      }

      if (ilBatch.length > 0) {
        const data = await ilBulkMutation.mutateAsync({ items: ilBatch });
        recordSaved('itemLocation', succeededIds(data));
        if (data.summary.failed > 0) {
          failedResults = data.results;
          throw new Error(`Failed to save ${data.summary.failed} item location(s)`);
        }
      }

      finishSaveSuccess({
        hasPoStaleChanges,
        rowCount: editing.changedCells.size,
      });
    } catch (error) {
      if (error.message === 'cancelled') {
        // User cancelled PO conflict dialog — revert grid without error
        refresh({ purge: false });
        return;
      }
      console.error("Error saving changes:", error);
      const someSaved = [...getChangedCellsRef().values()].some(isSaved);
      // Rows the server refused say why; any other failure has no reason
      // to show.
      const message = failedResults
        ? failedRowsMessage(t, failedResults)
        : t(someSaved ? "itemSetup.messages.savePartialFailed" : "itemSetup.messages.saveFailed");
      if (someSaved) {
        // Some rows persisted, others failed. Drop only the saved cells —
        // KEEP the rest so the merchant can retry without re-typing them
        // (one row can carry edits for several targets).
        showToast(message, "warning");
        clearCellsByPredicate(isSaved);
      } else {
        showToast(message, "error");
      }
    } finally {
      setSaving(false);
    }
  };

  useKeyboardSave(handleSaveChanges, editing.isDirty && !saving && !currencyGate.isOpen);
  const unsavedGuard = useUnsavedChangesGuard(editing.isDirty);

  const handleDiscardChanges = () => {
    clearAllEdits();
    refresh({ purge: false });
    showToast(t("common.changesDiscarded"), "info");
  };


  const openAddDialog = () => {
    setAddForm({ itemCode: '', sku: '', itemName: '', itemType: '', brand: '', barcode: '', productCategory: null, user: null, supplier: null, sellPrice: '', costPrice: '', npiFlag: true, active: true, location: null });
    setAddDialogOpen(true);
  };

  const addLengths = lengthLimits(addForm, ADD_ITEM_FIELD_MAX, t);

  const handleAddItem = async () => {
    if (!addForm.itemCode.trim() || !addForm.itemName.trim()) return;
    const payload = {
      item_code: addForm.itemCode.trim(),
      shopify_sku: addForm.sku.trim(),
      item_name: addForm.itemName.trim(),
      item_type: addForm.itemType.trim(),
      brand: addForm.brand.trim(),
      barcode: addForm.barcode.trim(),
      product_category_id: addForm.productCategory?.id || null,
      user_id: addForm.user,
      // Births the item's first supplier relation as its default (assign). The
      // cost below is denominated in this supplier's currency — the dialog blocks
      // a cost entry until a supplier is picked, and the backend enforces the same
      // invariant at the cost-write seam.
      default_supplier_id: addForm.supplier?.supplier_id || null,
      current_sell_price: addForm.sellPrice ? parseNumber(addForm.sellPrice) : null,
      current_cost_price_supplier_ccy: addForm.costPrice ? parseNumber(addForm.costPrice) : null,
      npi_flag: addForm.npiFlag,
      item_status: addForm.active ? 1 : 0,
    };

    try {
      const itemData = await createItemMutation.mutateAsync(payload);

      if (addForm.location) {
        try {
          await createItemLocationMutation.mutateAsync({
            // Wire input fields are ``item_id`` / ``location_id``.
            item_id: itemData.item_id,
            location_id: addForm.location,
          });
        } catch (ilError) {
          console.error('itemSetup add IL:', ilError.message);
          showToast('Item created but failed to assign to location', 'warning');
          setAddDialogOpen(false);
          return;
        }
      }

      showToast(t('itemSetup.messages.addSuccess'), 'success');
      setAddDialogOpen(false);
    } catch (error) {
      console.error('itemSetup add:', error.message);
      showToast(translateRequestError(error, t, 'itemSetup.messages.addFailed'), 'error');
    }
  };

  const shopifyNewProductUrl = isShopify && selectedStore?.domain
    ? `https://admin.shopify.com/store/${selectedStore.domain.replace('.myshopify.com', '')}/products/new`
    : null;

  return (
    <div className="page-container">
      {unsavedGuard}
      <PageBanner severity="warning" messages={[refDataError]} />
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.items')}
          toolbar={
            isShopify ? (
              <Button
                variant="contained"
                startIcon={<Plus size={ICON.md} />}
                href={shopifyNewProductUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('item_add_opened_in_channel')}
              >
                {t("itemSetup.addItem")}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Plus size={ICON.md} />}
                onClick={openAddDialog}
              >
                {t("itemSetup.addItem")}
              </Button>
            )
          }
          editActions={
            <SaveDiscardButtons
              count={editing.changedCells.size}
              saving={saving}
              onSave={handleSaveChanges}
              onDiscard={handleDiscardChanges}
            />
          }
          headerActions={
            <>
              <ImportButton
                entityKey="items"
                onClick={async () => {
                  // A bulk update refreshes the grid on success — pending
                  // edits would be silently clobbered without the guard.
                  if (await guardUnsavedChanges()) setImportDialogOpen(true);
                }}
              />
              <GridExportButton {...exportProps} />
            </>
          }
        >
          <GridPage>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: selectedProductCategory, set: setSelectedProductCategory, show: true }}
              locationFilter={{ selected: selectedLocation, set: setSelectedLocation }}
              actions={grouping.toggle}
            />

            {suppliers.length === 0 && (
              <PageBanner
                severity="info"
                messages={[t('dataHealth.noSuppliersItemSetup')]}
                actions={[{ label: t('dataHealth.noSuppliersAction'), onClick: () => openTab('/supplier-setup') }]}
              />
            )}

            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  // External filters (ItemFilterBar) bypass AG Grid, so
                  // the overlay can't infer this on its own.
                  filtersActive={selectedLocation !== 'all' || !!selectedProductCategory || !!itemFilter.filter}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...editableGridProps}
                    context={gridContext}
                    theme={gridTheme}
                    {...grouping.gridProps}
                    columnDefs={columnDefs}
                    defaultColDef={gridDefaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    getContextMenuItems={copyOnlyContextMenuItems}
                    rowModelType="serverSide"
                    {...pagination}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>

      {!isShopify && (
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} className="dialog-form">
          <DialogTitle>
            {t("itemSetup.addItem")}
            <IconButton onClick={() => setAddDialogOpen(false)} size="small"><X size={ICON.xl} /></IconButton>
          </DialogTitle>
          <DialogContent>
            {/* Section: Identification */}
            <Typography className="dialog-form-section">{t("itemSetup.sections.identification")}</Typography>
            <Box className="form-row-2col">
              <TextField
                label={t("itemSetup.columns.itemCode")}
                value={addForm.itemCode}
                onChange={(e) => setAddForm(prev => ({ ...prev, itemCode: e.target.value }))}
                size="small"
                autoFocus
                required
                className="flex-1"
                {...addLengths.propsFor('itemCode')}
              />
              <TextField
                label={t("itemSetup.columns.sku")}
                value={addForm.sku}
                onChange={(e) => setAddForm(prev => ({ ...prev, sku: e.target.value }))}
                size="small"
                className="flex-1"
                {...addLengths.propsFor('sku')}
              />
            </Box>
            <TextField
              label={t("itemSetup.columns.itemName")}
              value={addForm.itemName}
              onChange={(e) => setAddForm(prev => ({ ...prev, itemName: e.target.value }))}
              size="small"
              required
              {...addLengths.propsFor('itemName')}
            />

            {/* Section: Classification */}
            <Typography className="dialog-form-section">{t("itemSetup.sections.classification")}</Typography>
            <Box className="form-row-2col">
              <Autocomplete
                options={locations}
                value={locations.find(l => l.location_id === addForm.location) || null}
                getOptionLabel={(loc) => formatLocationLabel(loc, isShopify)}
                onChange={(_, v) => setAddForm(prev => ({ ...prev, location: v?.location_id || null }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("itemSetup.columns.location")}
                    size="small"
                    error={locationsError !== null}
                    helperText={locationsError !== null ? t('common.locationsLoadFailed') : undefined}
                  />
                )}
                size="small"
                className="flex-1"
              />
              <ProductCategoryPicker
                value={addForm.productCategory}
                onChange={(cat) => setAddForm(prev => ({ ...prev, productCategory: cat }))}
                label={t("itemSetup.columns.productCategory")}
                size="small"
                className="flex-1"
              />
            </Box>
            <Box className="form-row-2col">
              <TextField
                label={t("itemSetup.columns.itemType")}
                value={addForm.itemType}
                onChange={(e) => setAddForm(prev => ({ ...prev, itemType: e.target.value }))}
                size="small"
                className="flex-1"
              />
              <Autocomplete
                options={users}
                value={users.find(u => u.id === addForm.user) || null}
                getOptionLabel={(o) => `${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email || ''}
                onChange={(_, v) => setAddForm(prev => ({ ...prev, user: v?.id || null }))}
                renderInput={(params) => <TextField {...params} label={t("itemSetup.columns.user")} size="small" />}
                size="small"
                className="flex-1"
              />
            </Box>

            {/* Section: Pricing & Planning */}
            <Typography className="dialog-form-section">{t("itemSetup.sections.pricing")}</Typography>
            <Box className="form-row-2col">
              {/* Default supplier — births the first relation on create and, since
                  cost is stored in the supplier's currency, gates the cost field
                  below. Sell price stays store-currency, so it needs no supplier. */}
              <Autocomplete
                options={suppliers}
                value={addForm.supplier}
                getOptionLabel={(o) => o.supplier_name || ''}
                isOptionEqualToValue={(o, v) => o.supplier_id === v?.supplier_id}
                onChange={(_, v) => setAddForm(prev => ({
                  ...prev,
                  supplier: v,
                  // Clearing the supplier clears the now-uninterpretable cost.
                  costPrice: v ? prev.costPrice : '',
                }))}
                renderInput={(params) => <TextField {...params} label={t("itemSetup.columns.defaultSupplier")} size="small" />}
                size="small"
                className="flex-1"
              />
              <TextField
                label={t("itemSetup.columns.sellPrice")}
                value={addForm.sellPrice}
                onChange={(e) => setAddForm(prev => ({ ...prev, sellPrice: e.target.value }))}
                size="small"
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  ...(selectedStore?.currency && {
                    input: {
                      startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' } }}>{selectedStore.currency}</InputAdornment>,
                    },
                  }),
                }}
                className="flex-1"
              />
            </Box>
            <Box className="form-row-2col">
              <TextField
                label={t("itemSetup.columns.costPrice")}
                value={addForm.costPrice}
                onChange={(e) => setAddForm(prev => ({ ...prev, costPrice: e.target.value }))}
                size="small"
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  // Adornment shows the cost's unit: the picked supplier's
                  // currency (a supplier always has one), else the store's.
                  ...(selectedStore?.currency && {
                    input: {
                      startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' } }}>{addForm.supplier ? addForm.supplier.default_currency.currency_code : selectedStore.currency}</InputAdornment>,
                    },
                  }),
                }}
                className="flex-1"
              />
              <Box className="flex-1" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box className="form-row-2col">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addForm.active}
                      onChange={(e) => setAddForm(prev => ({ ...prev, active: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={t("itemSetup.columns.active")}
                  slotProps={{ typography: { variant: 'body2' } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addForm.npiFlag}
                      onChange={(e) => setAddForm(prev => ({ ...prev, npiFlag: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={t("itemSetup.columns.npi")}
                  slotProps={{ typography: { variant: 'body2' } }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t("common.tooltips.npi")}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setAddDialogOpen(false)}>{t("btn.cancel")}</Button>
            <Button
              variant="contained"
              onClick={handleAddItem}
              disabled={!addForm.itemCode.trim() || !addForm.itemName.trim() || addLengths.tooLong}
              loading={addLoading}
            >
              {t("itemSetup.addItem")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmDialog {...dialogProps} />
      <ItemSuppliersDialog
        item={suppliersDialogItem}
        onClose={() => setSuppliersDialogItem(null)}
        costGate={currencyGate}
      />
      <BulkImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        importHook={importHook}
        entityKey="items"
      />
      <CurrencyChangeConfirmModal {...currencyGate.modalProps} />
    </div>
  );
}

export default ItemSetup;
